import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import Button from '../../components/ui/Button';
import TabNavigation from '../../components/ui/TabNavigation';
import WaveformVisualizer from './components/WaveformVisualizer';
import PlaybackControls from './components/PlaybackControls';
import VolumeControl from './components/VolumeControl';
import QueueManager from './components/QueueManager';
import SocialInteractions from './components/SocialInteractions';
import RelatedContent from './components/RelatedContent';
import ChapterMarkers from './components/ChapterMarkers';
import SleepTimer from './components/SleepTimer';

const AudioPlayerInterface = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const audioRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1.0);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [showQueue, setShowQueue] = useState(false);
  const [showChapters, setShowChapters] = useState(false);
  const [showSleepTimer, setShowSleepTimer] = useState(false);

  const mockCurrentTrack = {
    id: "broadcast_001",
    title: "Философия присутствия: Разговор о подлинности",
    artist: "Анна Волкова",
    artwork: "https://images.unsplash.com/photo-1625727250720-455c5da8b9aa",
    artworkAlt: "Минималистичная студия звукозаписи с винтажным микрофоном и теплым освещением на деревянном столе",
    channelId: "channel_001",
    duration: 3600,
    description: "Глубокое погружение в концепцию аутентичного голоса и медиа-присутствия. Обсуждаем, как технологии меняют наше восприятие реальности и как сохранить подлинность в цифровом мире.",
    tags: ["философия", "осознанность", "медиа", "аутентичность"],
    listeners: "2.3K",
    publishedDate: "04.12.2025"
  };

  const mockQueue = [
  {
    id: "broadcast_001",
    title: "Философия присутствия: Разговор о подлинности",
    artist: "Анна Волкова",
    artwork: "https://images.unsplash.com/photo-1511891261770-ac6b69bfbe8e",
    artworkAlt: "Минималистичная студия звукозаписи с винтажным микрофоном"
  },
  {
    id: "broadcast_002",
    title: "Звуки острова: Медитативное путешествие",
    artist: "Дмитрий Соколов",
    artwork: "https://images.unsplash.com/photo-1574436249375-2578071e472a",
    artworkAlt: "Тропический остров с пальмами на закате у океана"
  },
  {
    id: "broadcast_003",
    title: "Тишина между словами",
    artist: "Елена Морозова",
    artwork: "https://images.unsplash.com/photo-1603469508499-d7a4e4c86b53",
    artworkAlt: "Абстрактная композиция с волнами и минималистичными формами"
  }];


  const mockChapters = [
  {
    id: "ch_001",
    title: "Введение: Что такое аутентичность?",
    timestamp: 0,
    formattedTime: "0:00",
    description: "Знакомство с темой и основными концепциями подлинного присутствия"
  },
  {
    id: "ch_002",
    title: "Цифровая идентичность и реальность",
    timestamp: 600,
    formattedTime: "10:00",
    description: "Как социальные сети влияют на наше восприятие себя"
  },
  {
    id: "ch_003",
    title: "Практики осознанности в медиа",
    timestamp: 1800,
    formattedTime: "30:00",
    description: "Конкретные техники для сохранения подлинности"
  },
  {
    id: "ch_004",
    title: "Вопросы и ответы",
    timestamp: 3000,
    formattedTime: "50:00",
    description: "Ответы на вопросы слушателей"
  }];


  const mockComments = [
  {
    id: "comment_001",
    author: "Михаил К.",
    text: "Невероятно глубокий разговор! Спасибо за честность и открытость",
    timestamp: "2 часа назад"
  },
  {
    id: "comment_002",
    author: "Светлана П.",
    text: "Эта тема очень резонирует со мной. Особенно часть про цифровую идентичность",
    timestamp: "5 часов назад"
  },
  {
    id: "comment_003",
    author: "Алексей Н.",
    text: "Отличный эпизод! Буду пересматривать практики осознанности",
    timestamp: "1 день назад"
  }];


  const mockRecommendations = [
  {
    id: "rec_001",
    title: "Медитация на звуках природы",
    host: "Ирина Белова",
    artwork: "https://images.unsplash.com/photo-1540787924427-057dbd3e424c",
    artworkAlt: "Спокойный лес с утренним туманом и солнечными лучами",
    duration: "45 мин",
    listeners: "1.8K",
    channelId: "channel_002"
  },
  {
    id: "rec_002",
    title: "Разговоры о смысле: Поиск цели",
    host: "Павел Иванов",
    artwork: "https://images.unsplash.com/photo-1683453979821-ef699c16cbc2",
    artworkAlt: "Человек смотрит на горизонт с вершины горы на рассвете",
    duration: "1 час 15 мин",
    listeners: "3.1K",
    channelId: "channel_003"
  },
  {
    id: "rec_003",
    title: "Минимализм в жизни и мышлении",
    host: "Ольга Смирнова",
    artwork: "https://images.unsplash.com/photo-1666696319287-a199c7bc2c64",
    artworkAlt: "Минималистичный интерьер с белыми стенами и простой мебелью",
    duration: "52 мин",
    listeners: "2.5K",
    channelId: "channel_004"
  }];


  useEffect(() => {
    const audio = audioRef?.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio?.currentTime);
    const updateDuration = () => setDuration(audio?.duration);
    const handleEnded = () => handleNext();

    audio?.addEventListener('timeupdate', updateTime);
    audio?.addEventListener('loadedmetadata', updateDuration);
    audio?.addEventListener('ended', handleEnded);

    return () => {
      audio?.removeEventListener('timeupdate', updateTime);
      audio?.removeEventListener('loadedmetadata', updateDuration);
      audio?.removeEventListener('ended', handleEnded);
    };
  }, []);

  const handlePlayPause = () => {
    const audio = audioRef?.current;
    if (!audio) return;

    if (isPlaying) {
      audio?.pause();
    } else {
      audio?.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (e) => {
    const audio = audioRef?.current;
    if (!audio) return;

    const seekTime = parseFloat(e?.target?.value);
    audio.currentTime = seekTime;
    setCurrentTime(seekTime);
  };

  const handleVolumeChange = (newVolume) => {
    const audio = audioRef?.current;
    if (!audio) return;

    setVolume(newVolume);
    audio.volume = newVolume;
    setIsMuted(newVolume === 0);
  };

  const handleMuteToggle = () => {
    const audio = audioRef?.current;
    if (!audio) return;

    const newMutedState = !isMuted;
    setIsMuted(newMutedState);
    audio.muted = newMutedState;
  };

  const handleSpeedChange = (newSpeed) => {
    const audio = audioRef?.current;
    if (!audio) return;

    setPlaybackSpeed(newSpeed);
    audio.playbackRate = newSpeed;
  };

  const handleSkipBackward = () => {
    const audio = audioRef?.current;
    if (!audio) return;

    audio.currentTime = Math.max(0, audio?.currentTime - 15);
  };

  const handleSkipForward = () => {
    const audio = audioRef?.current;
    if (!audio) return;

    audio.currentTime = Math.min(audio?.duration, audio?.currentTime + 15);
  };

  const handlePrevious = () => {
    if (currentTrackIndex > 0) {
      setCurrentTrackIndex(currentTrackIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentTrackIndex < mockQueue?.length - 1) {
      setCurrentTrackIndex(currentTrackIndex + 1);
    }
  };

  const handleChapterClick = (timestamp) => {
    const audio = audioRef?.current;
    if (!audio) return;

    audio.currentTime = timestamp;
    setCurrentTime(timestamp);
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs?.toString()?.padStart(2, '0')}`;
  };

  return (
    <>
      <Helmet>
        <title>Аудиоплеер - Radio Co Im</title>
        <meta name="description" content="Погрузитесь в мир аутентичного звука с расширенными функциями воспроизведения и социальным взаимодействием" />
      </Helmet>
      <div className="audio-player-page min-h-screen bg-background">
        <TabNavigation />

        <main className="page-content max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-24 lg:pb-8">
          <audio ref={audioRef} src="/assets/audio/sample.mp3" />

          <div className="player-layout grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="main-player-section lg:col-span-2 space-y-6">
              <div className="player-card bg-card rounded-lg border border-border p-8 shadow-soft">
                <div className="player-artwork-container mb-6">
                  <div className="artwork-wrapper aspect-square max-w-md mx-auto rounded-lg overflow-hidden shadow-soft-lg">
                    <Image
                      src={mockCurrentTrack?.artwork}
                      alt={mockCurrentTrack?.artworkAlt}
                      className="w-full h-full object-cover" />

                  </div>
                </div>

                <div className="player-info text-center mb-6">
                  <h1 className="text-2xl lg:text-3xl font-heading font-bold text-foreground mb-2">
                    {mockCurrentTrack?.title}
                  </h1>
                  <button
                    onClick={() => navigate(`/channel-profile?id=${mockCurrentTrack?.channelId}`)}
                    className="text-lg text-primary hover:text-primary/80 transition-colors duration-200">

                    {mockCurrentTrack?.artist}
                  </button>
                  <div className="flex items-center justify-center gap-4 mt-3 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Icon name="Users" size={16} color="currentColor" />
                      {mockCurrentTrack?.listeners}
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="Calendar" size={16} color="currentColor" />
                      {mockCurrentTrack?.publishedDate}
                    </span>
                  </div>
                </div>

                <div className="player-waveform mb-6">
                  <WaveformVisualizer
                    currentTime={currentTime}
                    duration={duration}
                    chapters={mockChapters}
                    isPlaying={isPlaying} />

                </div>

                <div className="player-progress mb-6">
                  <input
                    type="range"
                    min="0"
                    max={duration || 0}
                    value={currentTime}
                    onChange={handleSeek}
                    className="w-full h-2 bg-muted rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-soft"
                    aria-label="Прогресс воспроизведения" />

                  <div className="flex justify-between mt-2">
                    <span className="text-sm font-mono text-muted-foreground">
                      {formatTime(currentTime)}
                    </span>
                    <span className="text-sm font-mono text-muted-foreground">
                      {formatTime(duration)}
                    </span>
                  </div>
                </div>

                <div className="player-controls-wrapper mb-6">
                  <PlaybackControls
                    isPlaying={isPlaying}
                    onPlayPause={handlePlayPause}
                    onSkipBackward={handleSkipBackward}
                    onSkipForward={handleSkipForward}
                    onPrevious={handlePrevious}
                    onNext={handleNext}
                    playbackSpeed={playbackSpeed}
                    onSpeedChange={handleSpeedChange} />

                </div>

                <div className="player-actions flex items-center justify-between">
                  <VolumeControl
                    volume={volume}
                    onVolumeChange={handleVolumeChange}
                    isMuted={isMuted}
                    onMuteToggle={handleMuteToggle} />


                  <div className="action-buttons flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setShowChapters(!showChapters)}
                      aria-label="Показать главы">

                      <Icon name="List" size={20} color="var(--color-foreground)" />
                    </Button>

                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setShowQueue(!showQueue)}
                      aria-label="Показать очередь">

                      <Icon name="ListMusic" size={20} color="var(--color-foreground)" />
                    </Button>

                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setShowSleepTimer(!showSleepTimer)}
                      aria-label="Таймер сна">

                      <Icon name="Moon" size={20} color="var(--color-foreground)" />
                    </Button>

                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Добавить в избранное">

                      <Icon name="Heart" size={20} color="var(--color-foreground)" />
                    </Button>

                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Поделиться">

                      <Icon name="Share2" size={20} color="var(--color-foreground)" />
                    </Button>
                  </div>
                </div>
              </div>

              {showChapters &&
              <ChapterMarkers
                chapters={mockChapters}
                currentTime={currentTime}
                onChapterClick={handleChapterClick} />

              }

              {showQueue &&
              <QueueManager
                queue={mockQueue}
                currentIndex={currentTrackIndex}
                onPlay={(index) => setCurrentTrackIndex(index)}
                onRemove={(index) => console.log('Remove', index)}
                onReorder={(newQueue) => console.log('Reorder', newQueue)} />

              }

              <div className="broadcast-description bg-card rounded-lg border border-border p-6">
                <h2 className="text-lg font-heading font-semibold text-foreground mb-3">
                  Описание эпизода
                </h2>
                <p className="text-foreground leading-relaxed mb-4">
                  {mockCurrentTrack?.description}
                </p>
                <div className="tags-container flex flex-wrap gap-2">
                  {mockCurrentTrack?.tags?.map((tag) =>
                  <span
                    key={tag}
                    className="tag px-3 py-1 bg-primary/10 text-primary text-sm rounded-full">

                      #{tag}
                    </span>
                  )}
                </div>
              </div>

              <SocialInteractions
                comments={mockComments}
                onReact={(type) => console.log('React:', type)}
                onComment={(text) => console.log('Comment:', text)}
                onShare={() => console.log('Share')} />

            </div>

            <div className="sidebar-section space-y-6">
              {showSleepTimer &&
              <SleepTimer
                onTimerSet={(minutes) => console.log('Timer set:', minutes)}
                onTimerCancel={() => setShowSleepTimer(false)} />

              }

              <RelatedContent
                recommendations={mockRecommendations}
                onPlayRecommendation={(item) => console.log('Play:', item)} />

            </div>
          </div>
        </main>
      </div>
    </>);

};

export default AudioPlayerInterface;